import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type {
  ConnectionSummary,
  ConnectionTestResult,
  ConnectionType,
} from '@crowdex/plugin-contracts';
import { Badge } from '@crowdex/ui/components/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { EmptyState, InlineMessage, LoadingState } from '@crowdex/ui/components/feedback';
import { toast } from '@crowdex/ui/components/toaster';
import { cn } from '@crowdex/ui/lib/utils';
import { ChevronRight, Plus } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { settingButtonClass } from '@/components/settings-page/setting-controls';
import { announceConnectionsChanged } from '@/lib/plugins/connection-events';
import { ConnectionForm, connectionProblem, type Editing, type Listing } from './hub-connections';
import { ServiceLogo } from './plugin-logo';

const message = (error: unknown) => (error instanceof Error ? error.message : String(error));

export type ConnectionState = {
  label: string;
  /** `attention`: it waits on the user (sign in again), amber as everything that does. */
  tone: 'success' | 'attention' | 'destructive' | 'muted';
  /** The account must be connected again before what uses it works. */
  reconnect: boolean;
};

/**
 * What a row's Status says of a connection, a state only: Connected, Expired → Reconnect, or its
 * problem (Needs setup…). Being the default is a badge beside its name, not a state.
 */
export function connectionState(connection: ConnectionSummary): ConnectionState {
  if (connection.status === 'needs_reauthorization')
    return { label: 'Expired', tone: 'attention', reconnect: true };
  if (connection.status === 'revoked')
    return { label: 'Revoked', tone: 'attention', reconnect: true };
  if (connection.declaration === 'changed')
    return { label: 'Its plugin changed how it connects', tone: 'attention', reconnect: true };
  const problem = connectionProblem(connection);
  if (problem !== null)
    return {
      label: problem,
      tone: connection.declaration === 'missing' ? 'muted' : 'destructive',
      reconnect: false,
    };
  return {
    label:
      connection.account === 'crowlingo' ? 'Connected with your Crowlingo account' : 'Connected',
    tone: 'success',
    reconnect: false,
  };
}

const TONE_CLASS: Record<ConnectionState['tone'], string> = {
  success: 'text-success',
  attention: 'text-attention',
  destructive: 'text-destructive',
  muted: 'text-muted-foreground',
};

function ownerLabel(connection: ConnectionSummary) {
  return connection.owner.kind === 'organization' ? 'For my organization' : 'For me';
}

/** Check, make default, edit or remove an account. */
function ManageMenu({
  connection,
  type,
  onEdit,
  onChanged,
  onError,
  trigger,
}: {
  connection: ConnectionSummary;
  type: ConnectionType | undefined;
  onEdit: () => void;
  onChanged: () => void;
  onError: (message: string) => void;
  trigger: ReactNode;
}) {
  // Each action says it was done, or why not.
  const act = (run: () => Promise<unknown>, done: string) =>
    void run().then(
      () => {
        toast.success(done);
        onChanged();
      },
      (reason: unknown) => onError(message(reason)),
    );
  const name = `${type?.title ?? connection.type} · ${connection.name}`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {type?.test !== undefined && connection.declaration === 'current' && (
          <DropdownMenuItem
            onSelect={() =>
              act(async () => {
                const { result } = await apiFetch<{ result: ConnectionTestResult }>(
                  `/api/connections/${connection.id}/test`,
                  { method: 'POST' },
                );
                if (!result.ok) throw new Error(result.message);
              }, `${name} works`)
            }
          >
            Check it works
          </DropdownMenuItem>
        )}
        {connection.canEdit && !connection.isDefault && (
          <DropdownMenuItem
            onSelect={() =>
              act(
                () => apiFetch(`/api/connections/${connection.id}/default`, { method: 'PUT' }),
                `${name} is the default account`,
              )
            }
          >
            Make default
          </DropdownMenuItem>
        )}
        {connection.canEdit && type !== undefined && (
          <DropdownMenuItem onSelect={onEdit}>
            {connection.authKind === 'oauth2' ? 'Reconnect' : 'Edit'}
          </DropdownMenuItem>
        )}
        {connection.canEdit && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              onSelect={() => {
                if (
                  !window.confirm(`Remove ${name}? What uses it stops working until reconnected.`)
                )
                  return;
                act(
                  () => apiFetch(`/api/connections/${connection.id}`, { method: 'DELETE' }),
                  `${name} removed`,
                );
              }}
            >
              Remove
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A connection type as `/api/connections/types` lists it: with its logo when the app has one. */
export type ConnectionTypeView = ConnectionType & { logoUrl?: string };

/** The default connection of its service: a small badge beside its name. */
function DefaultBadge() {
  return (
    <Badge size="sm" variant="outline">
      Default
    </Badge>
  );
}

/** The hub's connections and the types of the enabled plugins, kept up to date. */
export function useConnections() {
  const [types, setTypes] = useState<ConnectionTypeView[] | null>(null);
  const [listing, setListing] = useState<Listing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const load = useCallback(async () => {
    try {
      const [nextTypes, nextListing] = await Promise.all([
        apiFetch<ConnectionTypeView[]>('/api/connections/types'),
        apiFetch<Listing>('/api/connections'),
      ]);
      setTypes(nextTypes);
      setListing(nextListing);
      setError(null);
    } catch (reason) {
      setError(message(reason));
    }
  }, []);
  useEffect(() => {
    void load();
    // A sign-in finishes in another window: the list follows when the user comes back.
    const reload = () => void load();
    window.addEventListener('focus', reload);
    return () => window.removeEventListener('focus', reload);
  }, [load]);
  return { types, listing, error, editing, setEditing, load };
}

export type ConnectionsData = ReturnType<typeof useConnections>;

/**
 * "Connect an account" (D32): the services the user's plugins can use and no account connects
 * yet first, under their label, then the others (another account of a connected service).
 */
export function ConnectAccountMenu({ data }: { data: ConnectionsData }) {
  const types = data.types ?? [];
  const connected = new Set((data.listing?.connections ?? []).map((item) => item.type));
  const unconnected = types.filter((type) => !connected.has(type.id));
  const others = types.filter((type) => connected.has(type.id));
  // One filled button per screen: an expired account's Reconnect while one shows, else this.
  const reconnecting = (data.listing?.connections ?? []).some(
    (connection) => connectionState(connection).reconnect,
  );
  const item = (type: ConnectionTypeView) => (
    <DropdownMenuItem key={type.id} onSelect={() => data.setEditing({ type, connection: null })}>
      <ServiceLogo logoUrl={type.logoUrl} size="small" />
      {type.title}
    </DropdownMenuItem>
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Connect an account"
          className={settingButtonClass(reconnecting ? 'outline' : 'primary')}
          type="button"
        >
          <Plus aria-hidden className="size-4" />
          <span className="max-md:hidden">Connect an account</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-96 min-w-56 overflow-y-auto">
        {types.length === 0 && (
          <DropdownMenuItem disabled>No plugin uses a connection yet</DropdownMenuItem>
        )}
        {unconnected.length > 0 && (
          <DropdownMenuLabel className="font-normal text-muted-foreground">
            Your plugins can use
          </DropdownMenuLabel>
        )}
        {unconnected.map(item)}
        {unconnected.length > 0 && others.length > 0 && <DropdownMenuSeparator />}
        {others.length > 0 && (
          <DropdownMenuLabel className="font-normal text-muted-foreground">
            Another account
          </DropdownMenuLabel>
        )}
        {others.map(item)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Connections (D32, phone 29): one connection per row with its state, an expired one with
 * Reconnect. The services the user's plugins can use are offered by "Connect an account".
 */
export function ConnectionsView({
  data,
  phone = false,
  focus = null,
}: {
  data: ConnectionsData;
  phone?: boolean;
  /**
   * The account an app asked to reconnect (`view.open({ connect, connection })`): its row is
   * marked and shown, and its sign-in starts once.
   */
  focus?: string | null;
}) {
  const { types, listing, error, editing, setEditing, load } = data;
  const [actionError, setActionError] = useState<string | null>(null);
  const started = useRef<string | null>(null);
  useEffect(() => {
    if (focus === null || types === null || listing === null || started.current === focus) return;
    const connection = listing.connections.find((candidate) => candidate.id === focus);
    if (connection === undefined) return;
    started.current = focus;
    [...document.querySelectorAll<HTMLElement>('[data-connection]')]
      .find((element) => element.dataset['connection'] === focus)
      ?.scrollIntoView({ block: 'center' });
    const type = types.find((candidate) => candidate.id === connection.type);
    if (connection.canEdit && type !== undefined) setEditing({ type, connection });
  }, [focus, listing, setEditing, types]);
  const changed = () => {
    setActionError(null);
    void load();
    announceConnectionsChanged();
  };

  if (error !== null)
    return (
      <InlineMessage
        actions={
          <button className={settingButtonClass()} onClick={() => void load()} type="button">
            Retry
          </button>
        }
        title="Couldn't load your connections."
        tone="error"
      >
        {error}
      </InlineMessage>
    );
  if (types === null || listing === null) return <LoadingState>Loading connections…</LoadingState>;

  const typeOf = (connection: ConnectionSummary) =>
    types.find((type) => type.id === connection.type);
  const titleOf = (connection: ConnectionSummary) => typeOf(connection)?.title ?? connection.type;
  const logoOf = (connection: ConnectionSummary) => typeOf(connection)?.logoUrl;
  const edit = (connection: ConnectionSummary) => {
    const type = typeOf(connection);
    if (type !== undefined) setEditing({ type, connection });
  };
  const reconnectButton = (connection: ConnectionSummary) => (
    <button
      className={settingButtonClass('primary')}
      disabled={!connection.canEdit || typeOf(connection) === undefined}
      onClick={() => edit(connection)}
      type="button"
    >
      Reconnect
    </button>
  );

  const rows =
    listing.connections.length === 0 ? (
      <EmptyState
        description="Connect an account: plugins and agents act with it."
        title="No connection yet"
      />
    ) : phone ? (
      <>
        {listing.connections
          .filter((connection) => connectionState(connection).reconnect)
          .map((connection) => (
            <div
              className={cn(
                'mb-4 flex items-center gap-3 rounded-2xl border border-attention bg-card px-4 py-3',
                focus === connection.id && 'ring-2 ring-attention',
              )}
              data-connection={connection.id}
              key={connection.id}
            >
              <ServiceLogo logoUrl={logoOf(connection)} />
              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 items-center gap-2 font-semibold text-base">
                  <span className="min-w-0 truncate">
                    {titleOf(connection)} · {connection.name}
                  </span>
                  {connection.isDefault && <DefaultBadge />}
                </span>
                <span className="block text-attention text-sm">
                  {connectionState(connection).label}
                </span>
              </span>
              {reconnectButton(connection)}
            </div>
          ))}
        <p className="mb-2 px-1 text-muted-foreground text-sm">Connected</p>
        <div className="mb-5 overflow-hidden rounded-2xl border bg-card">
          {listing.connections
            .filter((connection) => !connectionState(connection).reconnect)
            .map((connection) => {
              const state = connectionState(connection);
              return (
                <ManageMenu
                  connection={connection}
                  key={connection.id}
                  onChanged={changed}
                  onEdit={() => edit(connection)}
                  onError={setActionError}
                  trigger={
                    <button
                      className={cn(
                        'flex w-full items-center gap-3 border-hairline border-b px-4 py-3 text-left last:border-b-0',
                        focus === connection.id && 'bg-control',
                      )}
                      data-connection={connection.id}
                      type="button"
                    >
                      <ServiceLogo logoUrl={logoOf(connection)} />
                      <span className="min-w-0 flex-1">
                        <span className="flex min-w-0 items-center gap-2 text-base">
                          <span className="min-w-0 truncate">
                            {titleOf(connection)} · {connection.name}
                          </span>
                          {connection.isDefault && <DefaultBadge />}
                        </span>
                        <span
                          className={cn(
                            'block text-sm',
                            state.tone === 'success'
                              ? 'text-muted-foreground'
                              : TONE_CLASS[state.tone],
                          )}
                        >
                          {ownerLabel(connection)}
                          {state.tone !== 'success' && ` · ${state.label}`}
                        </span>
                      </span>
                      <ChevronRight aria-hidden className="size-4 text-subtle-foreground" />
                    </button>
                  }
                  type={typeOf(connection)}
                />
              );
            })}
        </div>
      </>
    ) : (
      <table className="mb-7 w-full overflow-hidden rounded-2xl bg-card ring-1 ring-border text-sm">
        <thead>
          <tr className="border-hairline border-b text-left text-muted-foreground text-xs">
            <th className="px-4 py-2.5 font-normal">Connection</th>
            <th className="px-4 py-2.5 font-normal">Who can use it</th>
            <th className="px-4 py-2.5 font-normal" colSpan={2}>
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {listing.connections.map((connection) => {
            const state = connectionState(connection);
            return (
              <tr
                aria-current={focus === connection.id ? 'true' : undefined}
                className={cn(
                  'border-hairline border-b last:border-b-0',
                  focus === connection.id && 'bg-control',
                )}
                data-connection={connection.id}
                key={connection.id}
              >
                <td className="px-4 py-2.5">
                  <span className="flex items-center gap-3">
                    <ServiceLogo logoUrl={logoOf(connection)} size="small" />
                    <span className="min-w-0">
                      <span className="flex min-w-0 items-center gap-2 text-foreground">
                        <span className="min-w-0 truncate">{titleOf(connection)}</span>
                        {connection.isDefault && <DefaultBadge />}
                      </span>
                      <span className="block truncate text-muted-foreground text-xs">
                        {connection.name}
                      </span>
                    </span>
                  </span>
                </td>
                <td className="px-4 py-2.5 text-foreground">{ownerLabel(connection)}</td>
                <td className={cn('px-4 py-2.5', TONE_CLASS[state.tone])}>{state.label}</td>
                <td className="px-4 py-2.5 text-right">
                  {state.reconnect ? (
                    reconnectButton(connection)
                  ) : (
                    <ManageMenu
                      connection={connection}
                      onChanged={changed}
                      onEdit={() => edit(connection)}
                      onError={setActionError}
                      trigger={
                        <button
                          aria-label={`Manage ${titleOf(connection)} · ${connection.name}`}
                          className={settingButtonClass()}
                          type="button"
                        >
                          Manage
                        </button>
                      }
                      type={typeOf(connection)}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );

  return (
    <div>
      {actionError && (
        <InlineMessage className="mb-4" tone="error">
          {actionError}
        </InlineMessage>
      )}
      {rows}
      {editing !== null && (
        <ConnectionForm
          editing={editing}
          logo={<ServiceLogo logoUrl={(editing.type as ConnectionTypeView).logoUrl} />}
          onClose={() => setEditing(null)}
          onSaved={() => {
            toast.success(`${editing.type.title} saved`);
            setEditing(null);
            changed();
          }}
          organization={listing.organization}
        />
      )}
    </div>
  );
}
